<template>
  <section class="template-import">
    <div class="section-heading">
      <h2>开票信息</h2>
      <div class="heading-actions">
        <button type="button" class="heading-action" @click="$emit('view-imports')">
          <van-icon name="orders-o" />
          <span>已导入申请</span>
        </button>
        <button
          v-if="selectedTemplate"
          type="button"
          class="heading-action"
          :disabled="disabled || downloading"
          @click="downloadTemplate"
        >
          <van-loading v-if="downloading" size="16px" />
          <van-icon v-else name="down" />
          <span>下载模板</span>
        </button>
      </div>
    </div>

    <InputField
      type="select"
      label="导入模板"
      align="right"
      :disabled="disabled || templateLoading"
      :value="selectedTemplateName"
      :placeholder="templateLoading ? '加载中...' : '请选择导入模板'"
      :options="templateOptions"
      @oninputchange="selectTemplate"
    />

    <div v-if="templateError" class="status-row status-error">
      <van-icon name="warning-o" />
      <span>{{ templateError }}</span>
      <button type="button" @click="$emit('retry-templates')">重试</button>
    </div>
    <div v-else-if="!templateLoading && !templates.length" class="status-row">
      <van-icon name="info-o" />
      <span>当前主体暂无可用的申请单导入模板</span>
    </div>

    <div class="upload-heading">
      <strong>上传 Excel</strong>
      <span>同一外部单据号的多行明细将生成一张开票申请</span>
    </div>

    <div v-if="fileName" class="selected-file">
      <van-icon name="description" />
      <span class="file-name">{{ displayFileName }}</span>
      <button type="button" aria-label="删除Excel" :disabled="disabled" @click="clearFile">
        <van-icon name="delete-o" />
      </button>
    </div>

    <van-uploader
      v-else
      :disabled="disabled || uploading || !selectedTemplate"
      :before-read="beforeRead"
      :after-read="afterRead"
      accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    >
      <div class="upload-trigger">
        <van-loading v-if="uploading" size="20px" />
        <van-icon v-else name="upgrade" />
        <span>{{ uploading ? '上传中...' : '选择 Excel 文件' }}</span>
      </div>
    </van-uploader>
  </section>
</template>

<script>
import InputField from '@/components/InputField'
import {
  downloadInvoiceImportTemplate,
  uploadInvoiceBatchFile
} from '@/services/InvoiceBatchImportService'
import { Icon, Loading, Notify, Uploader } from 'vant'

const MAX_FILE_SIZE = 10 * 1024 * 1024

export default {
  name: 'InvoiceTemplateImport',
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    templates: {
      type: Array,
      default: () => []
    },
    templateLoading: {
      type: Boolean,
      default: false
    },
    templateError: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      uploading: false,
      downloading: false,
      uploadRequestId: 0
    }
  },
  computed: {
    templateOptions () {
      return this.templates.map(item => ({
        title: item.templateName || '未命名模板',
        value: item.id
      }))
    },
    selectedTemplate () {
      return this.templates.find(item => String(item.id) === String(this.value.templateId)) || null
    },
    selectedTemplateName () {
      return this.selectedTemplate ? this.selectedTemplate.templateName : ''
    },
    fileName () {
      return this.value.fileName || ''
    },
    displayFileName () {
      return this.value.originalFileName || this.value.fileName || ''
    }
  },
  methods: {
    emitValue (patch) {
      this.$emit('input', {
        ...this.value,
        ...patch
      })
    },
    selectTemplate (templateId) {
      if (String(templateId) === String(this.value.templateId)) return
      this.uploadRequestId += 1
      this.uploading = false
      this.$emit('uploading-change', false)
      this.emitValue({
        templateId,
        fileName: '',
        originalFileName: '',
        fileDirectory: ''
      })
    },
    async downloadTemplate () {
      if (this.downloading || !this.selectedTemplate) return
      this.downloading = true
      try {
        await downloadInvoiceImportTemplate(this.selectedTemplate)
      } catch (error) {
        Notify({ type: 'danger', message: error.message || '模板下载失败' })
      } finally {
        this.downloading = false
      }
    },
    beforeRead (file) {
      const selectedFile = Array.isArray(file) ? file[0] : file
      const fileName = (selectedFile && selectedFile.name) || ''
      if (!/\.xlsx?$/i.test(fileName)) {
        Notify({ type: 'warning', message: '仅支持 xls 或 xlsx 格式文件' })
        return false
      }
      if (selectedFile.size > MAX_FILE_SIZE) {
        Notify({ type: 'warning', message: 'Excel 文件不能超过 10MB' })
        return false
      }
      return true
    },
    async afterRead (fileItem) {
      const selectedItem = Array.isArray(fileItem) ? fileItem[0] : fileItem
      const requestId = ++this.uploadRequestId
      this.uploading = true
      this.$emit('uploading-change', true)
      try {
        const result = await uploadInvoiceBatchFile(selectedItem.file)
        if (requestId !== this.uploadRequestId) return
        this.emitValue({
          fileName: result.fileName,
          originalFileName: result.originFileName,
          fileDirectory: result.fileDirectory
        })
      } catch (error) {
        if (requestId === this.uploadRequestId) {
          this.clearFile()
          Notify({ type: 'danger', message: error.message || 'Excel上传失败' })
        }
      } finally {
        if (requestId === this.uploadRequestId) {
          this.uploading = false
          this.$emit('uploading-change', false)
        }
      }
    },
    clearFile () {
      this.uploadRequestId += 1
      this.uploading = false
      this.$emit('uploading-change', false)
      this.emitValue({
        fileName: '',
        originalFileName: '',
        fileDirectory: ''
      })
    }
  },
  beforeDestroy () {
    this.uploadRequestId += 1
    this.uploading = false
  },
  components: {
    InputField,
    [Icon.name]: Icon,
    [Loading.name]: Loading,
    [Uploader.name]: Uploader
  }
}
</script>

<style lang="less" scoped>
.template-import {
  box-sizing: border-box;
  width: 96%;
  margin: 0 auto 16px;
  padding: 0 24px 32px;
  background: #fff;
  border-radius: 4px;
}

.section-heading {
  min-height: 88px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  h2 {
    margin: 0;
    color: #3e4966;
    font-size: 30px;
    line-height: 88px;
  }
}

.heading-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.heading-action {
  display: flex;
  align-items: center;
  padding: 12px 0 12px 20px;
  border: 0;
  background: transparent;
  color: #3378ff;
  font-size: 24px;

  > i,
  > .van-loading {
    margin-right: 8px;
  }
}

.status-row {
  display: flex;
  align-items: center;
  min-height: 64px;
  color: #7a879a;
  font-size: 24px;
  line-height: 34px;

  > i {
    flex: none;
    margin-right: 10px;
  }

  > span {
    min-width: 0;
    flex: 1;
  }

  button {
    flex: none;
    border: 0;
    background: transparent;
    color: #3378ff;
    font-size: 24px;
  }
}

.status-error {
  color: #e44b4b;
}

.upload-heading {
  display: flex;
  flex-direction: column;
  margin: 28px 0 20px;

  strong {
    color: #3e4966;
    font-size: 28px;
  }

  span {
    margin-top: 8px;
    color: #7a879a;
    font-size: 24px;
    line-height: 36px;
  }
}

.upload-trigger,
.selected-file {
  box-sizing: border-box;
  width: 100%;
  min-height: 92px;
  display: flex;
  align-items: center;
  padding: 20px 24px;
  background: #f5f7fa;
  color: #3378ff;
  font-size: 28px;

  > i,
  > .van-loading {
    flex: none;
    margin-right: 16px;
  }
}

.selected-file {
  color: #3e4966;

  .file-name {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    width: 56px;
    height: 56px;
    flex: none;
    border: 0;
    background: transparent;
    color: #7a879a;
    font-size: 32px;
  }
}

/deep/ .van-uploader,
/deep/ .van-uploader__input-wrapper {
  width: 100%;
}

/deep/ .van-cell {
  padding-right: 0;
  padding-left: 0;
}
</style>
